import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { reportsApi, type EventListRowInput, type ReportData } from '../../api/reports.js';
import { EmptyBox, ErrorBox, Field, Pagination, SearchInput, Spinner } from '../../components/ui.js';
import { useUrlSearch, useUrlUpdate } from '../../lib/hooks.js';
import { dateOnly, datetime, num, text, safeExternalUrl } from '../../lib/format.js';

type Row = Record<string, unknown>;
type ModalState = { mode: 'create' | 'edit' | 'copy'; row?: Row };

const STATE_TABS = [
  { value: '1', label: '진행', tone: 'active' },
  { value: '3', label: '대기', tone: 'waiting' },
  { value: '2', label: '종료', tone: 'ended' },
] as const;

function str(value: unknown): string {
  return value === null || value === undefined ? '' : String(value);
}

function dateInput(value: unknown): string {
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(str(value));
  return match && match[1] !== '0000-00-00' ? match[1]! : '';
}

function todayKorea(): string {
  return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Seoul' }).format(new Date());
}

function dayDiff(from: string, to: string): number {
  const start = Date.parse(`${from}T00:00:00Z`);
  const end = Date.parse(`${to}T00:00:00Z`);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return 0;
  return Math.round((end - start) / 86_400_000);
}

function scheduleInfo(row: Row) {
  const start = dateInput(row.start_date);
  const end = dateInput(row.end_date);
  const today = todayKorea();
  if (!start || !end) return { label: '기간 미등록', progress: 0, tone: 'muted' };
  if (today < start) return { label: `시작 D-${dayDiff(today, start)}`, progress: 0, tone: 'waiting' };
  if (today > end) return { label: `${dayDiff(end, today)}일 전 종료`, progress: 100, tone: 'ended' };
  const duration = Math.max(dayDiff(start, end), 1);
  const elapsed = Math.max(dayDiff(start, today), 0);
  return { label: `종료 D-${dayDiff(today, end)}`, progress: Math.min(100, (elapsed / duration) * 100), tone: 'active' };
}

export function EventListPage() {
  const [params, setParams] = useSearchParams();
  const update = useUrlUpdate(params, setParams);
  const [q, setQ] = useUrlSearch(params, update, 'q');
  const state = params.get('state') ?? '1';
  const startFrom = params.get('startFrom') ?? '';
  const endTo = params.get('endTo') ?? '';
  const page = Number(params.get('page') || '1');
  const [modal, setModal] = useState<ModalState | null>(null);

  const queryParams = useMemo(() => ({
    q, state, startFrom, endTo, page, perPage: 100, sort: 'seq', dir: 'DESC',
  }), [q, state, startFrom, endTo, page]);
  const list = useQuery({
    queryKey: ['report', 'event-list', queryParams],
    queryFn: () => reportsApi.data('event-list', queryParams),
  });
  const rows = list.data?.items ?? [];
  const currentState = STATE_TABS.find((tab) => tab.value === state) ?? STATE_TABS[0];

  return (
    <section className="event-list-page">
      <header className="page-head event-list-head">
        <div>
          <span className="event-list-eyebrow">CONTENT OPERATIONS</span>
          <h1 className="page-title">이벤트 관리</h1>
          <p className="page-desc">일정과 진행 상태를 한눈에 확인하고 목록에서 바로 수정합니다.</p>
        </div>
        <div className="page-head__actions">
          {list.isFetching && <span className="page-head__busy">갱신 중…</span>}
          <a className="btn btn--ghost btn--sm" href={reportsApi.exportUrl('event-list', queryParams)}>CSV 내려받기</a>
          <button type="button" className="btn btn--primary btn--sm" onClick={() => setModal({ mode: 'create' })}>+ 이벤트 등록</button>
        </div>
      </header>

      <div className="event-list-statusbar">
        <div className={`event-list-statusbar__icon event-list-statusbar__icon--${currentState.tone}`}>●</div>
        <div><span>현재 보기</span><strong>{currentState.label} 이벤트</strong></div>
        <div><span>검색 결과</span><strong>{list.isPending ? '—' : `${num(list.data?.total ?? 0)}건`}</strong></div>
        <div><span>기준일</span><strong>{todayKorea()}</strong></div>
      </div>

      <div className="filters event-list-filters">
        <Field label="빠른 검색">
          <SearchInput value={q} onChange={setQ} placeholder="이벤트명, 담당자 또는 메모 검색" />
        </Field>
        <Field label="시작일 이후">
          <input className="input" type="date" value={startFrom} onChange={(event) => update({ startFrom: event.target.value, page: 1 })} />
        </Field>
        <Field label="종료일 이전">
          <input className="input" type="date" value={endTo} onChange={(event) => update({ endTo: event.target.value, page: 1 })} />
        </Field>
        <button type="button" className="btn btn--ghost filters__reset" onClick={() => setParams({ state: '1' }, { replace: true })}>조건 초기화</button>
      </div>

      <div className="event-list-nav">
        <div className="tab-group" aria-label="이벤트 상태">
          {STATE_TABS.map((tab) => (
            <button key={tab.value} type="button" className={state === tab.value ? 'tab-btn tab-btn--active' : 'tab-btn'}
              onClick={() => update({ state: tab.value, page: 1 })}>
              <span className={`event-list-tabdot event-list-tabdot--${tab.tone}`} />{tab.label}
            </button>
          ))}
        </div>
        <span className="event-list-result">최신 등록순 · 페이지당 100건</span>
      </div>

      {list.isPending ? <Spinner /> : list.isError ? (
        <ErrorBox error={list.error} onRetry={() => void list.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyBox>조건에 맞는 이벤트가 없습니다.</EmptyBox>
      ) : <EventTable data={list.data} rows={rows} page={page} onOpen={setModal} />}

      {list.data && list.data.totalPages > 1 && (
        <Pagination page={list.data.page} totalPages={list.data.totalPages} total={list.data.total}
          onChange={(next) => update({ page: next }, false)} />
      )}

      {modal && <EventFormModal state={modal} onClose={() => setModal(null)} />}
    </section>
  );
}

function EventTable({ data, rows, page, onOpen }: {
  data: ReportData; rows: Row[]; page: number; onOpen: (state: ModalState) => void;
}) {
  return (
    <div className="table-wrap event-list-table-wrap">
      <table className="table event-list-table">
        <thead><tr>
          <th>No</th><th>상태</th><th>이벤트 / 링크</th><th>진행 일정</th><th>담당자</th><th>메모</th><th>등록일</th><th>관리</th>
        </tr></thead>
        <tbody>{rows.map((row, index) => {
          const schedule = scheduleInfo(row);
          const stats = str(row.stats) || '미지정';
          const tone = stats === '진행' ? 'active' : stats === '대기' ? 'waiting' : 'ended';
          const eventUrl = safeExternalUrl(row.link);
          return (
            <tr key={str(row.seq) || index} className={`event-list-row event-list-row--${tone}`}>
              <td className="td-muted">{data.total - ((page - 1) * data.perPage + index)}</td>
              <td><span className={`event-list-state event-list-state--${tone}`}><i />{stats}</span></td>
              <td><div className="event-list-name">
                <strong>{text(str(row.eventname))}</strong>
                {eventUrl ? <a href={eventUrl} target="_blank" rel="noreferrer" title={str(row.link)}>↗ {str(row.link)}</a> : <span>{row.link ? '유효하지 않은 링크' : '연결 링크 없음'}</span>}
              </div></td>
              <td><div className="event-list-schedule">
                <div><strong>{dateOnly(str(row.start_date))}</strong><span>→</span><strong>{dateOnly(str(row.end_date))}</strong></div>
                <div className="event-list-progress"><i style={{ width: `${schedule.progress}%` }} /></div>
                <small className={`event-list-schedule__${schedule.tone}`}>{schedule.label}</small>
              </div></td>
              <td><span className="event-list-owner">{text(str(row.up_name))}</span></td>
              <td><span className="event-list-memo" title={str(row.memo)}>{text(str(row.memo))}</span></td>
              <td className="event-list-regdate">{datetime(str(row.regdate))}</td>
              <td><div className="event-list-actions">
                <button type="button" className="btn btn--ghost btn--sm" onClick={() => onOpen({ mode: 'edit', row })}>수정</button>
                <button type="button" className="btn btn--ghost btn--sm" onClick={() => onOpen({ mode: 'copy', row })}>복사</button>
              </div></td>
            </tr>
          );
        })}</tbody>
      </table>
    </div>
  );
}

function defaultForm(state: ModalState): EventListRowInput {
  const row = state.row ?? {};
  const today = todayKorea();
  return {
    eventname: str(row.eventname),
    stats: state.mode === 'copy' ? '대기' : (['진행', '종료', '대기'].includes(str(row.stats)) ? str(row.stats) as EventListRowInput['stats'] : '대기'),
    start_date: state.mode === 'create' ? today : dateInput(row.start_date),
    end_date: state.mode === 'create' ? today : dateInput(row.end_date),
    link: str(row.link),
    up_name: str(row.up_name),
    memo: str(row.memo),
  };
}

function EventFormModal({ state, onClose }: { state: ModalState; onClose: () => void }) {
  const [form, setForm] = useState<EventListRowInput>(() => defaultForm(state));
  const queryClient = useQueryClient();
  const seq = Number(state.row?.seq);
  const save = useMutation({
    mutationFn: () => state.mode === 'edit' ? reportsApi.updateEventListRow(seq, form) : reportsApi.createEventListRow(form),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['report', 'event-list'] });
      onClose();
    },
  });
  const valid = form.eventname.trim() !== '' && form.up_name.trim() !== '' && form.start_date !== '' && form.end_date !== '' && form.start_date <= form.end_date;
  const title = state.mode === 'edit' ? '이벤트 수정' : state.mode === 'copy' ? '이벤트 복사 등록' : '새 이벤트 등록';

  function set<K extends keyof EventListRowInput>(key: K, value: EventListRowInput[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel modal-panel--wide event-list-modal" role="dialog" aria-modal="true" aria-labelledby="event-list-modal-title" onClick={(event) => event.stopPropagation()}>
        <button type="button" className="modal-close" onClick={onClose} aria-label="닫기">×</button>
        <div className="event-list-modal__head">
          <span>{state.mode === 'edit' ? `EVENT #${seq}` : 'NEW EVENT'}</span>
          <h2 id="event-list-modal-title" className="modal-title">{title}</h2>
          <p>{state.mode === 'copy' ? '기존 내용을 복사했습니다. 일정과 상태를 확인한 뒤 등록해주세요.' : '목록에서 필요한 핵심 정보만 빠르게 변경합니다.'}</p>
        </div>
        <form className="form-stack" onSubmit={(event) => { event.preventDefault(); if (valid) save.mutate(); }}>
          <Field label="이벤트명">
            <input className="input event-list-modal__name" autoFocus value={form.eventname} onChange={(event) => set('eventname', event.target.value)} />
          </Field>
          <div className="row-3 event-list-modal__core">
            <Field label="상태">
              <select className="input" value={form.stats} onChange={(event) => set('stats', event.target.value as EventListRowInput['stats'])}>
                <option value="진행">진행</option><option value="대기">대기</option><option value="종료">종료</option>
              </select>
            </Field>
            <Field label="시작일">
              <input className="input" type="date" value={form.start_date} onChange={(event) => set('start_date', event.target.value)} />
            </Field>
            <Field label="종료일">
              <input className="input" type="date" min={form.start_date} value={form.end_date} onChange={(event) => set('end_date', event.target.value)} />
            </Field>
          </div>
          {form.start_date && form.end_date && form.start_date > form.end_date && <p className="error-box">종료일은 시작일보다 빠를 수 없습니다.</p>}
          <div className="row-2">
            <Field label="담당자">
              <input className="input" value={form.up_name} placeholder="담당자 전체 이름" onChange={(event) => set('up_name', event.target.value)} />
            </Field>
            <Field label="이벤트 링크">
              <input className="input" type="url" value={form.link} placeholder="https://" onChange={(event) => set('link', event.target.value)} />
            </Field>
          </div>
          <Field label="메모">
            <textarea className="input event-list-modal__memo" value={form.memo} placeholder="운영 시 확인할 내용이나 변경 사항" onChange={(event) => set('memo', event.target.value)} />
          </Field>
          {save.isError && <ErrorBox error={save.error} />}
          <div className="event-list-modal__footer">
            <span>* 이벤트명, 기간, 담당자는 필수입니다.</span>
            <div><button type="button" className="btn btn--ghost" onClick={onClose}>취소</button>
              <button type="submit" className="btn btn--primary" disabled={!valid || save.isPending}>{save.isPending ? '저장 중…' : state.mode === 'edit' ? '변경사항 저장' : '이벤트 등록'}</button></div>
          </div>
        </form>
      </div>
    </div>
  );
}
